import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { emptyTrashQuery, getTrashQuery } from '@/services/trash.service';
import { useGallerySelection } from '@/context/GallerySelectionContext';
import { useAuthenticatedQueryFn } from '@/hooks/useAuthenticatedQuery';
import { type BackendError } from '@/hooks/useFormErrorHandler';
import { type TrashNodesResponse } from '@/types/trash';
import { useToast } from '@/components/ui/use-toast';
import { error as errorLog } from '@/utilities/log';
import { QueryKey } from '@/lib/queryKeys';
import { useTranslations } from 'next-intl';

export const useTrash = (
  params: {
    pagination: { limit: number | null; offset: number | null };
    sort: { order: string; value: string };
    // queryString: string;
  },
  options?: object,
) => {
  const tToasts = useTranslations('toasts');
  const queryClient = useQueryClient();
  const getTrashQueryFn = useAuthenticatedQueryFn(getTrashQuery);
  const emptyTrashQueryFn = useAuthenticatedQueryFn(emptyTrashQuery);

  const { toast } = useToast();
  const { deselectAllItems } = useGallerySelection();

  const queryKey = [
    QueryKey.Trash,
    params.pagination,
    params.sort,
    // params.queryString
  ];

  const {
    data: listTrash,
    isLoading,
    isPending,
    isFetching,
    error,
  } = useQuery({
    queryKey,
    queryFn: () => getTrashQueryFn(params),
    placeholderData: keepPreviousData,
    // The global `refetchOnMount: false` would hand back the bin as you left it, however
    // long ago. No broadcast arrives while unsubscribed, so entering Trash has to refetch.
    refetchOnMount: 'always',
    ...options,
  });

  const { mutateAsync: emptyTrash, isPending: isEmptyTrashPending } = useMutation<
    TrashNodesResponse,
    BackendError,
    void,
    { previousNodes?: TrashNodesResponse }
  >({
    mutationFn: emptyTrashQueryFn,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });

      // Get current trash items before mutation
      const previousNodes = queryClient.getQueryData<TrashNodesResponse>(queryKey);

      return { previousNodes };
    },
    onSettled: () => {
      // Optimistically update UI: keep pagination but empty the trash list
      queryClient.setQueryData(queryKey, (oldData: TrashNodesResponse | undefined) =>
        oldData ? { ...oldData, trash: [] } : { trash: [], pagination: params.pagination },
      );

      toast({
        title: tToasts('emptyTrash.title'),
        description: tToasts('emptyTrash.description'),
      });

      deselectAllItems();
    },
    onError: (err, _, context) => {
      // Restore previous state if mutation fails
      queryClient.setQueryData(queryKey, context?.previousNodes);
      errorLog(err);
    },
  });

  return {
    listTrash,
    isLoading,
    isPending,
    isFetching,
    error,
    emptyTrash,
    isEmptyTrashPending,
  };
};
